import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  TextField,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  Edit as EditIcon,
  TwoWheeler as TwoWheelerIcon,
  AccountBalance as AccountBalanceIcon,
  VerifiedUser as VerifiedUserIcon,
  Payment as PaymentIcon,
  History as HistoryIcon,
  MoneyOff as MoneyOffIcon,
  Business as BusinessIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import {
  riderService,
  misService,
  vehicleHistoryService,
  payoutService,
  Rider,
  RiderKYC,
  RiderEarning,
  RiderEarningsSummary,
  VehicleAssignment,
} from '../services';
import ordersService, { Order as RiderOrderType, OrdersResponse } from '../services/ordersService';
import { RiderVehicleHistory } from '../services/vehicleHistoryService';
import {
  getCurrentRental,
  getRentalPayments,
} from '../services/evRentalService';
import type {
  RentalWithPayments,
} from '../types/evRental';

// Import the new modular components
import RiderProfileTab from '../components/rider/RiderProfileTab';
import RiderKYCTab from '../components/rider/RiderKYCTab';
import RiderEarningsTab from '../components/rider/RiderEarningsTab';
import RiderVehicleHistoryTab from '../components/rider/RiderVehicleHistoryTab';
import RiderOrderHistoryTab from '../components/rider/RiderOrderHistoryTab';
import RiderDeductionsTab from '../components/rider/RiderDeductionsTab';
import RiderPaymentHistoryTab from '../components/rider/RiderPaymentHistoryTab';
import RiderRegistrationCompleter from '../components/rider/RiderRegistrationCompleter';
import BankDetailsSection from '../components/rider/BankDetailsSection';
import RiderClientMappingsTab from '../components/rider/RiderClientMappingsTab';
import RiderServiceTicketsTab from '../components/rider/RiderServiceTicketsTab';

// Import EV Rental components
import {
  VehiclePreferenceSelector,
  RentalAssignmentDialog as EVRentalAssignmentDialog,
  RentalPaymentTab,
} from '../components/ev-rental';

// Import the dialog components
import DocumentPreviewDialog from '../components/rider/dialogs/DocumentPreviewDialog';
import VehicleAssignmentDialog from '../components/rider/dialogs/VehicleAssignmentDialog';
import VehicleUnassignmentDialog from '../components/rider/dialogs/VehicleUnassignmentDialog';
import KYCVerificationDialog from '../components/rider/dialogs/KYCVerificationDialog';
import SetFixedDepositDialog from '../components/rider/dialogs/SetFixedDepositDialog';
import CreatePaylinkDialog from '../components/rider/dialogs/CreatePaylinkDialog';
import EnhancedRiderForm from '../components/EnhancedRiderForm';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`rider-tabpanel-${index}`}
      aria-labelledby={`rider-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `rider-tab-${index}`,
    'aria-controls': `rider-tabpanel-${index}`,
  };
}

const API_BASE_URL = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '');

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getVehicleOwnership = (type?: string | null) => {
  if (type === 'EV') return 'Company Vehicle';
  if (type === 'NON-EV') return 'Own Vehicle';
  return 'Not Available';
};

const isClientMappingValid = (clientName?: string | null, clientRiderId?: string | null) => {
  const normalizedClientName = (clientName ?? '').trim();
  const normalizedClientRiderId = (clientRiderId ?? '').trim();
  const normalizedClientNameUpper = normalizedClientName.toUpperCase();

  const hasValidClientName = !!normalizedClientName &&
    !normalizedClientNameUpper.includes('CLIENT NOT FOUND') &&
    !normalizedClientNameUpper.includes('CLIENT NAME NOT FOUND');

  return hasValidClientName && !!normalizedClientRiderId;
};

const RETURN_TICKET_REASONS = [
  'Exiting',
  'Swapping Vehicle',
  'Shifting to Own Vehicle',
] as const;

const RETURN_TICKET_REASON_NOTES: Record<typeof RETURN_TICKET_REASONS[number], string> = {
  Exiting:
    'Select this if the rider is leaving our platform. After the vehicle is unassigned, the system will automatically mark the rider as Inactive.',
  'Swapping Vehicle':
    'Select this if the rider wants to swap the existing vehicle.',
  'Shifting to Own Vehicle':
    'Select this if the rider wants to work with their own vehicle. After the vehicle is unassigned, the system will automatically change the rider preference to Own Vehicle.',
};

const RiderDetail: React.FC = () => {
  const { riderId } = useParams<{ riderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  // State for rider data
  const [rider, setRider] = useState<Rider | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [tabValue, setTabValue] = useState<number>(0);

  useEffect(() => {
    if ((location.state as { tab?: number } | null)?.tab !== undefined) {
      setTabValue((location.state as { tab: number }).tab);
    }
  }, [location.state]);

  // State for KYC documents
  const [kycDocuments, setKycDocuments] = useState<RiderKYC[]>([]);
  const [kycLoading, setKycLoading] = useState<boolean>(false);

  // State for earnings
  const [earnings, setEarnings] = useState<RiderEarning[]>([]);
  const [deductions, setDeductions] = useState<any[]>([]);
  const [earningsSummary, setEarningsSummary] = useState<RiderEarningsSummary | null>(null);
  const [earningsPeriod, _setEarningsPeriod] = useState<'weekly' | 'monthly' | 'yearly'>('monthly');

  // State for vehicle history
  const [vehicleHistory, setVehicleHistory] = useState<RiderVehicleHistory[]>([]);
  const [vehicleHistoryLoading, setVehicleHistoryLoading] = useState<boolean>(false);

  // State for payment history
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState<boolean>(false);

  // State for Order History
  const [orderHistory, setOrderHistory] = useState<RiderOrderType[]>([]);
  const [ordersLoading, setOrdersLoading] = useState<boolean>(false);
  const [orderPage, setOrderPage] = useState<number>(1);
  const [orderPageSize, _setOrderPageSize] = useState<number>(20);
  const [orderTotalPages, setOrderTotalPages] = useState<number>(1);
  // Date-only filters for order history
  const [filterDateFrom, setFilterDateFrom] = useState<string | undefined>(undefined);
  const [filterDateTo, setFilterDateTo] = useState<string | undefined>(undefined);

  // State for EV Rental
  const [currentRental, setCurrentRental] = useState<RentalWithPayments | null>(null);
  const [rentalLoading, setRentalLoading] = useState<boolean>(false);
  const [rentalAssignmentDialog, setRentalAssignmentDialog] = useState<boolean>(false);

  // State for Security Deposit
  const [sdWallet, setSdWallet] = useState<{
    totalSd: number;
    pendingSd: number;
    fixedDeposit: number | null;
  } | null>(null);
  const [pendingEarnings, setPendingEarnings] = useState<number>(0);

  // State for Payment Block Status
  const [paymentBlockStatus, setPaymentBlockStatus] = useState<any>(null);
  const [unblockDialogOpen, setUnblockDialogOpen] = useState(false);
  const [unblockReason, setUnblockReason] = useState('');
  const [unblocking, setUnblocking] = useState(false);
  const [deploymentTicketPayingSD, setDeploymentTicketPayingSD] = useState<string>('');
  const [deploymentTicketFixedSecurityDeposit, setDeploymentTicketFixedSecurityDeposit] = useState<string>('');
  const [deploymentTicketReason, setDeploymentTicketReason] = useState('');
  const [raisingDeploymentTicket, setRaisingDeploymentTicket] = useState(false);
  const [raisingReturnTicket, setRaisingReturnTicket] = useState(false);
  const [returnTicketReason, setReturnTicketReason] = useState('');
  const [returnTicketFeedback, setReturnTicketFeedback] = useState('');
  const [returnTicketUnassignmentReason, setReturnTicketUnassignmentReason] = useState('');
  const [returnTicketDialogOpen, setReturnTicketDialogOpen] = useState(false);

  // Dialog states
  const [documentPreviewDialog, setDocumentPreviewDialog] = useState<{
    open: boolean;
    documentUrl: string | null;
    documentType: string;
  }>({
    open: false,
    documentUrl: null,
    documentType: '',
  });

  const [vehicleAssignmentDialog, setVehicleAssignmentDialog] = useState<{
    open: boolean;
  }>({
    open: false,
  });

  const [vehicleUnassignmentDialog, setVehicleUnassignmentDialog] = useState<{
    open: boolean;
  }>({
    open: false,
  });

  const [kycVerificationDialog, setKycVerificationDialog] = useState<{
    open: boolean;
    kyc: RiderKYC | null;
  }>({
    open: false,
    kyc: null,
  });

  // Edit rider dialog state
  const [editRiderDialog, setEditRiderDialog] = useState<boolean>(false);

  // Set Fixed Deposit dialog state
  const [setFixedDepositDialog, setSetFixedDepositDialog] = useState<boolean>(false);

  // Create Paylink dialog state
  const [createPaylinkDialog, setCreatePaylinkDialog] = useState<boolean>(false);

  // Vehicle assignment state
  const [hubs, setHubs] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<VehicleAssignment[]>([]);
  const [vehicleSearch, setVehicleSearch] = useState<string>('');
  const [vehiclePage, setVehiclePage] = useState<number>(1);
  const [vehicleTotalPages, setVehicleTotalPages] = useState<number>(1);
  const [selectedHub, setSelectedHub] = useState<string>('');
  const [selectedVehicle, setSelectedVehicle] = useState<string>('');
  const [assignmentLoading, setAssignmentLoading] = useState<boolean>(false);
  const [dailyRent, setDailyRent] = useState<number | null>(null);
  const [dailyRentLoading, setDailyRentLoading] = useState<boolean>(false);
  const [vehicleSearchLoading, setVehicleSearchLoading] = useState<boolean>(false);
  const [vehicleSearchOpen, setVehicleSearchOpen] = useState<boolean>(false);

  // Fetch rider details
  const fetchRiderDetails = useCallback(async () => {
    if (!riderId) return;

    try {
      setLoading(true);
      const response = await riderService.getRiderById(riderId);

      if (response.success && response.data) {
        setRider(response.data);
      } else {
        enqueueSnackbar('Failed to load rider details', { variant: 'error' });
      }
    } catch (error: any) {
      console.error('Error fetching rider details:', error);
      enqueueSnackbar(error.message || 'Error loading rider details', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [riderId, enqueueSnackbar]);

  // Fetch KYC documents
  const fetchKYCDocuments = useCallback(async () => {
    if (!riderId) return;

    try {
      setKycLoading(true);
      const response = await riderService.getRiderKYC(riderId);

      if (response.success) {
        setKycDocuments(response.data);
      }
    } catch (error: any) {
      console.error('Error fetching KYC documents:', error);
    } finally {
      setKycLoading(false);
    }
  }, [riderId]);

  // Fetch earnings and deductions
  const fetchEarnings = useCallback(async () => {
    if (!riderId || !rider?.publicRiderId) return;

    try {
      // Fetch MIS earnings rows and deduction rows by public rider ID and the existing earnings summary
      const [misResponse, deductionResponse, summaryResponse] = await Promise.all([
        // MIS endpoint expects riderPublicRiderId
        (misService as any).getEarningsByRider(rider.publicRiderId, { limit: 50 }),
        (misService as any).getDeductionsByRider(rider.publicRiderId, { limit: 50 }),
        riderService.getRiderEarningsSummary(riderId, earningsPeriod),
      ]);

      // Normalize MIS earnings response which could come in multiple shapes
      if (!misResponse) {
        console.warn('[RiderDetail] Empty MIS earnings response', misResponse);
        setEarnings([]);
      } else if (Array.isArray(misResponse)) {
        setEarnings(misResponse);
      } else if (misResponse.data && Array.isArray(misResponse.data)) {
        setEarnings(misResponse.data);
      } else if (misResponse.success && Array.isArray(misResponse.data)) {
        setEarnings(misResponse.data);
      } else {
        console.warn('[RiderDetail] Unexpected MIS earnings response shape:', misResponse);
        setEarnings([]);
      }

      // Normalize deduction response
      if (!deductionResponse) {
        console.warn('[RiderDetail] Empty MIS deduction response', deductionResponse);
        setDeductions([]);
      } else if (Array.isArray(deductionResponse)) {
        setDeductions(deductionResponse);
      } else if (deductionResponse.data && Array.isArray(deductionResponse.data)) {
        setDeductions(deductionResponse.data);
      } else if (deductionResponse.success && Array.isArray(deductionResponse.data)) {
        setDeductions(deductionResponse.data);
      } else {
        console.warn('[RiderDetail] Unexpected MIS deduction response shape:', deductionResponse);
        setDeductions([]);
      }

      if (summaryResponse.success) {
        setEarningsSummary(summaryResponse.data);
      }
    } catch (error: any) {
      console.error('Error fetching earnings:', error);
    }
  }, [riderId, rider?.publicRiderId, earningsPeriod]);

  // Fetch vehicle history
  const fetchVehicleHistory = useCallback(async () => {
    if (!riderId) return;

    try {
      setVehicleHistoryLoading(true);
      const response = await vehicleHistoryService.getRiderVehicleHistory(riderId);

      if (response.success) {
        setVehicleHistory(response.data);
      }
    } catch (error: any) {
      console.error('Error fetching vehicle history:', error);
    } finally {
      setVehicleHistoryLoading(false);
    }
  }, [riderId]);

  // Fetch order history for selected rider
  const fetchOrderHistory = useCallback(async (page: number = 1) => {
    if (!rider?.publicRiderId) return;
    try {
      setOrdersLoading(true);
      const response: OrdersResponse = await ordersService.getOrdersByRider(
        rider.publicRiderId as string,
        page,
        orderPageSize,
        {
          dateFrom: filterDateFrom,
          dateTo: filterDateTo,
        }
      );
      setOrderHistory(response.data || []);
      if (response.pagination) {
        setOrderPage(response.pagination.page);
        setOrderTotalPages(response.pagination.totalPages);
      }
    } catch (error: any) {
      console.error('Error fetching rider orders:', error);
      setOrderHistory([]);
      setOrderTotalPages(1);
    } finally {
      setOrdersLoading(false);
    }
  }, [rider?.publicRiderId, orderPageSize]);
  // Apply and clear filter handlers
  const handleApplyFilters = () => {
    setOrderPage(1);
    fetchOrderHistory(1);
  };

  const handleClearFilters = () => {
    setFilterDateFrom(undefined);
    setFilterDateTo(undefined);
    setOrderPage(1);
    fetchOrderHistory(1);
  };

  // Fetch payment history (payouts) by rider public ID
  const fetchPaymentHistory = useCallback(async (page = 1, limit = 50) => {
    if (!rider?.publicRiderId) return;

    try {
      setPaymentHistoryLoading(true);
      // Payouts endpoint returns { total, page, limit, data }
      const res: any = await payoutService.getPayoutsByRider(rider.publicRiderId, { page, limit });

      const payouts = res?.data || res || [];

      // Map payout records to the Payment interface used by RiderPaymentHistoryTab
      const mapped = (payouts as any[]).map((p) => ({
        id: p.id,
        date: p.transactionDate,
        amount: p.amount,
        status: p.status === 'SUCCESS' ? 'completed' : p.status === 'FAILED' ? 'failed' : 'pending',
        description: p.remarks || `${p.clientName || ''} / ${p.beneficiaryName || ''}`,
        orderId: undefined,
        utrNumber: p.utrNumber || undefined,
        clientName: p.clientName || undefined,
        beneficiaryAccountNo: p.beneficiaryAccountNo || undefined,
        transactionType: p.transactionType || undefined,
      }));

      setPaymentHistory(mapped);
    } catch (error: any) {
      console.error('Error fetching payment history (payouts):', error);
      setPaymentHistory([]);
      enqueueSnackbar(error?.response?.data?.message || error?.message || 'Failed to load payout history', { variant: 'error' });
    } finally {
      setPaymentHistoryLoading(false);
    }
  }, [rider?.publicRiderId, enqueueSnackbar]);

  // Fetch EV Rental data
  const fetchRentalData = useCallback(async () => {
    if (!riderId) return;

    try {
      setRentalLoading(true);
      const rental = await getCurrentRental(riderId);

      if (rental) {
        // Fetch payments for this rental
        const paymentsData = await getRentalPayments(riderId, rental.id);

        // Combine rental with payments
        const rentalWithPayments: RentalWithPayments = {
          ...rental,
          payments: paymentsData.payments || [],
        };

        setCurrentRental(rentalWithPayments);
      } else {
        setCurrentRental(null);
      }
    } catch (error: any) {
      console.error('Error fetching rental data:', error);
      setCurrentRental(null);
    } finally {
      setRentalLoading(false);
    }
  }, [riderId]);

  // Fetch security deposit wallet data
  const fetchSecurityDepositWallet = useCallback(async () => {
    if (!riderId) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/v1/security-deposit/riders/${riderId}/wallet`,
        { headers: getAuthHeaders() }
      );

      if (!response.ok) {
        setSdWallet(null);
        return;
      }

      const data = await response.json();
      const wallet = data?.data || data;
      const fixedDeposit = wallet?.fixedDeposit == null ? null : Number(wallet.fixedDeposit);

      setSdWallet({
        totalSd: Number(wallet?.totalSd ?? 0),
        pendingSd: Number(wallet?.pendingSd ?? 0),
        fixedDeposit: Number.isFinite(fixedDeposit) ? fixedDeposit : null,
      });
    } catch (error) {
      console.error('Error fetching security deposit wallet:', error);
      setSdWallet(null);
    }
  }, [riderId]);

  // Fetch the pending amount used by the vehicle unassignment flow.
  const fetchPendingEarnings = useCallback(async () => {
    if (!riderId) return;

    try {
      const response = await riderService.getRiderEarningsSummary(riderId, 'monthly');
      const summaryData = response?.data as unknown as { pending?: number };
      const pending = Number(summaryData?.pending ?? 0);
      setPendingEarnings(Number.isFinite(pending) ? pending : 0);
    } catch (error) {
      console.error('Error fetching pending rider earnings:', error);
      setPendingEarnings(0);
    }
  }, [riderId]);

  // Fetch payment block status
  const fetchPaymentBlockStatus = useCallback(async () => {
    if (!riderId) return;
    try {
      const response = await fetch(
        `${API_BASE_URL}/v1/riders/payments/riders/${riderId}/block-status`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) return;

      const data = await response.json();
      if (data.success) {
        setPaymentBlockStatus(data.data);
      }
    } catch (error) {
      console.error('Error fetching payment block status:', error);
    }
  }, [riderId]);

  // Handle unblock payment
  const handleUnblockPayment = async () => {
    if (!unblockReason.trim()) {
      enqueueSnackbar('Please provide a reason for unblocking', { variant: 'warning' });
      return;
    }

    try {
      setUnblocking(true);
      const response = await fetch(
        `${API_BASE_URL}/v1/riders/payments/riders/${riderId}/unblock`,
        {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            unblockedBy: 'admin', // TODO: Get from auth context
            unblockReason: unblockReason.trim(),
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        enqueueSnackbar('Payment unblocked successfully', { variant: 'success' });
        setUnblockDialogOpen(false);
        setUnblockReason('');
        fetchPaymentBlockStatus();
      } else {
        enqueueSnackbar(data.message || 'Failed to unblock payment', { variant: 'error' });
      }
    } catch (error: any) {
      console.error('Error unblocking payment:', error);
      enqueueSnackbar('Failed to unblock payment', { variant: 'error' });
    } finally {
      setUnblocking(false);
    }
  };

  // Fetch hubs for vehicle assignment
  const fetchHubs = useCallback(async () => {
    try {
      console.log('🔵 [RiderDetail] Fetching hubs...');
      const response = await riderService.getHubs();
      console.log('🔵 [RiderDetail] Hubs response:', response);
      if (response.success) {
        console.log('✅ [RiderDetail] Setting hubs:', response.data.length, 'hubs');
        setHubs(response.data);
      } else {
        console.warn('⚠️ [RiderDetail] Hubs fetch failed:', response.message);
      }
    } catch (error) {
      console.error('❌ [RiderDetail] Error fetching hubs:', error);
    }
  }, []);

  // Fetch available vehicles based on selected hub
  const fetchAvailableVehicles = useCallback(
    async (options: { hubId?: string; search?: string; page?: number }) => {
      try {
        setVehicleSearchLoading(true);
        if (options.page === 1) {
          setVehicles([]);
        }
        const response = await riderService.getAvailableVehicles({
          hubId: options.hubId,
          search: options.search,
          page: options.page || 1,
          limit: 20,
        });
        if (response.success) {
          if (options.page === 1) {
            setVehicles(response.data);
          } else {
            setVehicles((prev) => [...prev, ...response.data]);
          }
          setVehicleTotalPages(response.pagination?.totalPages || 1);
          setVehiclePage(response.pagination?.page || 1);
        } else {
          console.warn('⚠️ [RiderDetail] Failed to fetch vehicles:', response.message);
          enqueueSnackbar(response.message || 'Failed to load vehicles', { variant: 'warning' });
        }
      } catch (error) {
        console.error('❌ [RiderDetail] Error fetching vehicles:', error);
        enqueueSnackbar('Error loading vehicles', { variant: 'error' });
      } finally {
        setVehicleSearchLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  const fetchFixedDailyRent = useCallback(async (vehicleId: string, hubId: string) => {
    if (!vehicleId || !hubId) {
      setDailyRent(null);
      return;
    }

    const selectedVehicleDetails = vehicles.find((vehicle) => vehicle.id === vehicleId);
    const selectedHubDetails = hubs.find((hub) => hub.id === hubId);

    if (!selectedVehicleDetails?.modelId || !selectedHubDetails?.name) {
      setDailyRent(null);
      return;
    }

    try {
      setDailyRentLoading(true);
      const response = await fetch(`${API_BASE_URL}/v1/security-deposit/fixed-daily-rent`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vehicleModelId: selectedVehicleDetails.modelId,
          hubName: selectedHubDetails.name,
          batteryConfig: selectedVehicleDetails.batteryConfiguration || undefined,
        }),
      });

      const data = await response.json();
      if (response.ok && data?.success) {
        const rentValue = Number(data?.data?.fixedDailyRent ?? 0);
        setDailyRent(Number.isFinite(rentValue) ? rentValue : null);
      } else {
        setDailyRent(null);
        console.warn('[RiderDetail] Failed to fetch fixed daily rent:', data?.message || response.statusText);
      }
    } catch (error: any) {
      console.error('[RiderDetail] Error fetching fixed daily rent:', error);
      setDailyRent(null);
    } finally {
      setDailyRentLoading(false);
    }
  }, [hubs, vehicles]);

  // Load data based on active tab
  useEffect(() => {
    fetchRiderDetails();
    fetchSecurityDepositWallet();
    fetchPendingEarnings();
    fetchPaymentBlockStatus();
  }, [fetchRiderDetails, fetchSecurityDepositWallet, fetchPendingEarnings, fetchPaymentBlockStatus]);

  useEffect(() => {
    switch (tabValue) {
      case 1: // KYC Documents
        fetchKYCDocuments();
        break;
      case 2: // Bank Details (handled by component)
        break;
      case 3: // EV Rental
        fetchRentalData();
        break;
      case 4: // Client Mappings
        break;
      case 5: // Earnings
        fetchEarnings();
        break;
      case 6: // Deductions
        fetchEarnings(); // same call fetches deduction rows as well
        break;
      case 7: // Payment History
        fetchPaymentHistory();
        break;
      case 8: // Vehicle History
        fetchVehicleHistory();
        break;
      case 9: // Order History
        fetchOrderHistory(1);
        break;
    }
  }, [tabValue, fetchKYCDocuments, fetchEarnings, fetchVehicleHistory, fetchPaymentHistory, fetchRentalData, fetchOrderHistory]);

  // If the order history tab is open and rider data loads later, fetch orders
  useEffect(() => {
    if (tabValue === 9 && rider?.publicRiderId) {
      fetchOrderHistory(1);
    }
  }, [rider?.publicRiderId, tabValue, fetchOrderHistory]);

  // Handle KYC document upload
  const handleKYCUpload = async (kycData: any, progressCallback?: (progress: number) => void) => {
    if (!riderId) return;

    try {
      const response = await riderService.submitKYC(
        riderId,
        kycData,
        progressCallback
      );

      if (response.success) {
        enqueueSnackbar('KYC document uploaded successfully', { variant: 'success' });
        fetchKYCDocuments();
        fetchRiderDetails(); // Refresh rider data to update KYC status
      }
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to upload KYC document', { variant: 'error' });
      throw error; // Re-throw to let the dialog handle it
    }
  };

  // Handle KYC verification
  const handleKYCVerify = async (status: 'verified' | 'rejected', notes: string) => {
    if (!riderId || !kycVerificationDialog.kyc) return;

    try {
      const response = await riderService.verifyKYC(
        riderId,
        kycVerificationDialog.kyc.id,
        status,
        notes
      );

      if (response.success) {
        enqueueSnackbar(
          `KYC document ${status === 'verified' ? 'verified' : 'rejected'} successfully`,
          { variant: 'success' }
        );
        fetchKYCDocuments();
        setKycVerificationDialog({ open: false, kyc: null });
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to verify KYC document';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  // Handle vehicle assignment
  const handleVehicleAssignment = async (
    assignedAt?: string,
    assigningOdometerCount?: number,
    images?: Record<string, File | null>,
  ) => {
    if (!riderId || !selectedVehicle) {
      enqueueSnackbar('Please select a vehicle', { variant: 'warning' });
      return;
    }

    try {
      setAssignmentLoading(true);
      const selectedVehicleDetails = vehicles.find((vehicle) => vehicle.id === selectedVehicle);
      const response = await riderService.assignVehicleToRider(
        riderId,
        {
          vehicleId: selectedVehicle,
          hubId: selectedHub || undefined,
          assignedAt: assignedAt,
          registrationNumber: selectedVehicleDetails?.registrationNumber?.trim() || undefined,
          vehicleMake: selectedVehicleDetails?.make || undefined,
          vehicleModel: selectedVehicleDetails?.model || undefined,
          dailyRent: dailyRent ?? null,
          AssigningOdometerCount: assigningOdometerCount,
        },
        images,
      );

      if (response.success) {
        enqueueSnackbar('Vehicle assigned successfully', { variant: 'success' });
        setVehicleAssignmentDialog({ open: false });
        setSelectedHub('');
        setSelectedVehicle('');
        setDailyRent(null);
        fetchRiderDetails();
        fetchVehicleHistory();
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to assign vehicle';
      const normalizedMessage = message
        .replace(/^Request failed with status code\s+\d+\.?/i, '')
        .trim();
      enqueueSnackbar(normalizedMessage || 'Failed to assign vehicle', { variant: 'error' });
    } finally {
      setAssignmentLoading(false);
    }
  };

  // Handle vehicle unassignment
  const handleUnassignVehicle = async (
    reason: string,
    riderFeedback?: string,
    images?: Record<string, File | null>,
    returningOdometerCount?: number
  ) => {
    if (!riderId) return;

    const noOfReturnTickets = rider?.NoOfReturnTickets ?? 0;
    if (noOfReturnTickets !== 1) {
      const message =
        noOfReturnTickets === 0
          ? 'No return ticket found for this rider. Vehicle unassignment is not allowed until exactly one return ticket exists.'
          : `Multiple return tickets found for this rider. Unassignment is allowed only when exactly one return ticket exists. Current return tickets: ${noOfReturnTickets}.`;
      enqueueSnackbar(message, { variant: 'warning' });
      return;
    }

    try {
      const response = await riderService.unassignVehicleFromRider(
        riderId,
        reason,
        riderFeedback,
        images,
        returningOdometerCount,
      );

      if (response.success) {
        enqueueSnackbar('Vehicle unassigned successfully', { variant: 'success' });
        setVehicleUnassignmentDialog({ open: false });
        fetchRiderDetails();
        fetchVehicleHistory();
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to unassign vehicle';
      const normalizedMessage = message
        .replace(/^Request failed with status code\s+\d+\.?/i, '')
        .trim();
      enqueueSnackbar(normalizedMessage || 'Failed to unassign vehicle', { variant: 'error' });
      throw error; // Re-throw to let dialog handle the error
    }
  };

  // Open unassignment dialog
  const handleOpenUnassignmentDialog = () => {
    if (!rider) return;

    const noOfReturnTickets = rider.NoOfReturnTickets ?? 0;
    if (noOfReturnTickets !== 1) {
      enqueueSnackbar(
        noOfReturnTickets === 0
          ? 'No return ticket found for this rider. Vehicle unassignment is not allowed until exactly one return ticket exists.'
          : `Multiple return tickets found for this rider. Unassignment is allowed only when exactly one return ticket exists. Current return tickets: ${noOfReturnTickets}.`,
        { variant: 'warning' }
      );
      return;
    }

    setVehicleUnassignmentDialog({ open: true });
  };

  const handleRaiseDeploymentTicket = async () => {
    if (!riderId || !rider) return;

    if (rider.kycStatus !== 'approved') {
      enqueueSnackbar('Deployment ticket can only be raised for riders whose KYC is approved.', { variant: 'warning' });
      return;
    }

    if (!deploymentTicketReason) {
      enqueueSnackbar('Please select a reason before raising the deployment ticket.', { variant: 'warning' });
      return;
    }

    const parsedPayingSD = deploymentTicketPayingSD.trim() ? Number(deploymentTicketPayingSD) : 0;
    if (
      deploymentTicketReason === 'NEW_RIDER' &&
      (!deploymentTicketPayingSD.trim() || !Number.isFinite(parsedPayingSD) || parsedPayingSD <= 0)
    ) {
      enqueueSnackbar('Paying SD is required for New Rider - Vehicle Deployment.', { variant: 'warning' });
      return;
    }

    if (
      deploymentTicketReason === 'NEW_RIDER' &&
      (!deploymentTicketFixedSecurityDeposit.trim() || !Number.isFinite(Number(deploymentTicketFixedSecurityDeposit)) || Number(deploymentTicketFixedSecurityDeposit) <= 0)
    ) {
      enqueueSnackbar('Fixed Security Deposit is required for New Rider - Vehicle Deployment.', { variant: 'warning' });
      return;
    }

    if (!Number.isFinite(parsedPayingSD) || parsedPayingSD < 0) {
      enqueueSnackbar('Please enter a valid Paying SD amount.', { variant: 'warning' });
      return;
    }

    const parsedFixedSecurityDeposit = deploymentTicketFixedSecurityDeposit.trim() ? Number(deploymentTicketFixedSecurityDeposit) : 0;
    if (!Number.isFinite(parsedFixedSecurityDeposit) || parsedFixedSecurityDeposit < 0) {
      enqueueSnackbar('Please enter a valid Fixed Security Deposit amount.', { variant: 'warning' });
      return;
    }

    if (parsedPayingSD > parsedFixedSecurityDeposit) {
      enqueueSnackbar('Paying SD cannot be greater than the Fixed Security Deposit.', { variant: 'warning' });
      return;
    }

    try {
      setRaisingDeploymentTicket(true);
      const response = await fetch(`${API_BASE_URL}/v1/deployement-return-tickets/`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          riderId: rider.id,
          payingSD: parsedPayingSD,
          fixedSecurityDeposit: parsedFixedSecurityDeposit,
          reason: deploymentTicketReason,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || data?.error || 'Failed to raise deployment ticket');
      }

      enqueueSnackbar(data?.message || 'Deployment ticket raised successfully', { variant: 'success' });
      setDeploymentTicketPayingSD('');
      setDeploymentTicketFixedSecurityDeposit('');
      setDeploymentTicketReason('');
      fetchRiderDetails();
    } catch (error: any) {
      const message = error?.message || 'Failed to raise deployment ticket';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setRaisingDeploymentTicket(false);
    }
  };

  const handleRaiseReturnTicket = async () => {
    if (!riderId || !rider) return;

    if (!rider.assignedVehicle) {
      enqueueSnackbar('Return ticket can only be raised when a vehicle is assigned to the rider.', { variant: 'warning' });
      return;
    }

    if (!returnTicketReason) {
      enqueueSnackbar('Please select a reason for returning the vehicle.', { variant: 'warning' });
      return;
    }

    try {
      setRaisingReturnTicket(true);
      const response = await fetch(`${API_BASE_URL}/v1/deployement-return-tickets/return/`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          riderId: rider.id,
          reason: returnTicketReason,
          riderfeedback: returnTicketFeedback.trim() || undefined,
          UnassignmentReason: returnTicketUnassignmentReason.trim() || undefined,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || data?.error || 'Failed to raise return ticket');
      }

      enqueueSnackbar(data?.message || 'Return ticket raised successfully', { variant: 'success' });
      setReturnTicketReason('');
      setReturnTicketFeedback('');
      setReturnTicketUnassignmentReason('');
      setReturnTicketDialogOpen(false);
      fetchRiderDetails();
    } catch (error: any) {
      const message = error?.message || 'Failed to raise return ticket';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setRaisingReturnTicket(false);
    }
  };

  // Handle opening vehicle assignment dialog
  // Handle vehicle assignment button click
  const handleAssignVehicle = () => {
    if (!rider) return;

    const noOfDeployedTickets = rider.NoOfDeployedTickets ?? 0;
    const canAssignVehicle = isClientMappingValid(rider.clientName, rider.clientRiderId) &&
      rider.needsEvRental &&
      rider.kycStatus === 'approved' &&
      rider.registrationStatus?.toLowerCase() === 'completed' &&
      rider.isActive &&
      noOfDeployedTickets === 1;

    if (!canAssignVehicle) {
      const invalidReasons: string[] = [];

      if (!isClientMappingValid(rider.clientName, rider.clientRiderId)) {
        invalidReasons.push('Client name and client rider ID are required and valid before assigning a vehicle.');
      }
      if (!rider.needsEvRental) {
        invalidReasons.push('Vehicle preference must be set to "Yes".');
      }
      if (rider.kycStatus !== 'approved') {
        invalidReasons.push(`KYC must be approved. Current status: ${rider.kycStatus}.`);
      }
      if (rider.registrationStatus?.toLowerCase() !== 'completed') {
        invalidReasons.push(`Registration must be completed. Current status: ${rider.registrationStatus}.`);
      }
      if (!rider.isActive) {
        invalidReasons.push('Rider must be active.');
      }
      if (noOfDeployedTickets !== 1) {
        invalidReasons.push(`Exactly one deployment ticket is required to assign a vehicle. Current deployed tickets: ${noOfDeployedTickets}.`);
      }

      enqueueSnackbar(invalidReasons.join(' '), { variant: 'warning' });
      return;
    }

    console.log('🚀 [RiderDetail] Opening vehicle assignment dialog');
    console.log('🚀 [RiderDetail] Current hubs:', hubs.length);
    console.log('🚀 [RiderDetail] Current vehicles:', vehicles.length);
    setVehicleAssignmentDialog({ open: true });
    console.log('🚀 [RiderDetail] Fetching hubs...');
    fetchHubs();
    console.log('🚀 [RiderDetail] Fetching available vehicles...');
    if (selectedHub) {
      fetchAvailableVehicles({ hubId: selectedHub, page: 1 });
    } else {
      setVehicles([]);
    }
  };

  // Handle navigate to vehicle detail page
  const handleNavigateToVehicle = (vehicleId: string) => {
    navigate(`/vehicles/${vehicleId}`);
  };

  // Handle edit rider
  const handleOpenEditRider = () => {
    setEditRiderDialog(true);
  };

  const handleCloseEditRider = () => {
    setEditRiderDialog(false);
  };

  const handleSaveRider = async (formData: any) => {
    if (!riderId) return;

    try {
      const response = await riderService.updateRider(riderId, formData);

      if (response.success) {
        enqueueSnackbar('Rider updated successfully', { variant: 'success' });
        handleCloseEditRider();
        fetchRiderDetails(); // Refresh rider data
      } else {
        enqueueSnackbar(response.message || 'Failed to update rider', { variant: 'error' });
      }
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to update rider', { variant: 'error' });
    }
  };

  const handleSaveProfileSection = async (data: import('../services/riderService').RiderProfileUpdateData | import('../services/riderService').RiderKYCDetailsUpdateData) => {
    if (!riderId) throw new Error('Rider ID is missing');
    const response = await riderService.updateRider(riderId, data);
    if (!response.success) throw new Error(response.message || 'Failed to update rider');
    // The update response uses database document names; preserve the normalized detail model.
    setRider((current) => current ? {
      ...current, ...data,
      phoneVerified: 'phone' in data && data.phone !== current.phone ? false : current.phoneVerified,
    } : current);
    enqueueSnackbar('Rider details updated successfully', { variant: 'success' });
  };

  const hasValidClientAssignment = rider ? isClientMappingValid(rider.clientName, rider.clientRiderId) : false;

  const isVehicleAssignmentDisabled = !!rider && (!rider.needsEvRental ||
    rider.kycStatus !== 'approved' ||
    rider.registrationStatus?.toLowerCase() !== 'completed' ||
    !rider.isActive ||
    !hasValidClientAssignment ||
    (rider.NoOfDeployedTickets ?? 0) !== 1);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleBack = () => {
    navigate('/rider-management');
  };

  // Handle EV Rental actions
  const handleOpenRentalAssignment = () => {
    setRentalAssignmentDialog(true);
  };

  const handleRentalCreated = () => {
    enqueueSnackbar('EV Rental assigned successfully', { variant: 'success' });
    setRentalAssignmentDialog(false);
    fetchRentalData();
    fetchRiderDetails(); // Refresh rider data to update rental status
  };

  const handlePreferenceSelected = (_preference: any, modelId: string, _ownVehicleType?: any) => {
    enqueueSnackbar('Vehicle preference saved successfully', { variant: 'success' });
    fetchRiderDetails(); // Refresh rider data
    // Auto-open rental assignment if rider selected a model
    if (modelId && !currentRental) {
      setTimeout(() => {
        handleOpenRentalAssignment();
      }, 500);
    }
  };

  const handlePaymentUpdated = () => {
    fetchRentalData(); // Refresh rental and payment data
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!rider) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Rider not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0.5, sm: 1.5, md: 3 }, overflowX: 'hidden' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          mb: { xs: 1.5, md: 3 },
          '& .MuiBreadcrumbs-ol': { flexWrap: 'wrap' },
          '& .MuiBreadcrumbs-li': { minWidth: 0 },
        }}
      >
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
        >
          Home
        </Link>
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleBack();
          }}
        >
          Rider Management
        </Link>
        <Typography color="text.primary">{rider.name || 'Rider Details'}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.25}
        sx={{ mb: { xs: 2, md: 3 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <ArrowBackIcon
            sx={{ cursor: 'pointer', flexShrink: 0, '&:hover': { color: 'primary.main' } }}
            onClick={handleBack}
          />
          <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' }, fontWeight: 800 }}>
            Rider Details
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            '& .MuiButton-root': {
              flex: { xs: '1 1 calc(50% - 8px)', sm: '0 0 auto' },
              minWidth: { xs: 0, sm: 64 },
              px: { xs: 1, md: 2 },
            },
          }}
        >
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleOpenEditRider}
          >
            Edit Rider
          </Button>
          {rider.assignedVehicle && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<CurrencyRupeeIcon />}
              onClick={() => setSetFixedDepositDialog(true)}
            >
              Set Fixed Deposit
            </Button>
          )}
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PaymentIcon />}
            onClick={() => setCreatePaylinkDialog(true)}
          >
            Create Paylink
          </Button>
        </Box>
      </Stack>

      {/* Rider Info Header Card */}
      <Paper
        sx={{
          p: { xs: 1.5, md: 3 },
          mb: { xs: 2, md: 3 },
          position: { xs: 'relative', md: 'sticky' },
          top: { md: 70 },
          zIndex: { md: 100 },
          backgroundColor: 'background.paper',
          boxShadow: 3,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={{ xs: 1.5, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
          <Avatar
            src={rider.selfie || undefined}
            sx={{ width: { xs: 64, md: 80 }, height: { xs: 64, md: 80 }, bgcolor: 'primary.main', alignSelf: { xs: 'center', sm: 'auto' } }}
          >
            {rider.name ? rider.name.charAt(0).toUpperCase() : <PersonIcon fontSize="large" />}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' }, fontWeight: 800, minWidth: 0, overflowWrap: 'anywhere' }}>
                {rider.name || 'No Name'}
              </Typography>
              {rider.publicRiderId && (
                <Chip
                  label={rider.publicRiderId}
                  color="primary"
                  variant="outlined"
                  size="medium"
                  sx={{ fontWeight: 'bold', fontSize: '0.875rem', maxWidth: { xs: '100%', sm: 220 }, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                />
              )}
              {rider.workType && (
                <Typography variant="body1" color="text.secondary" sx={{ ml: { md: 'auto' }, fontWeight: 500 }}>
                  {rider.workType === 'FULL_TIME' ? 'Full-Time' : 'Part-Time'}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={rider.isActive ? 'Active' : 'Inactive'}
                color={rider.isActive ? 'success' : 'error'}
                size="small"
              />
              <Chip
                label={`Registration: ${rider.registrationStatus}`}
                color={rider.registrationStatus?.toLowerCase() === 'completed' ? 'success' : 'warning'}
                size="small"
              />
              <Chip
                label={`KYC: ${rider.kycStatus}`}
                color={rider.kycStatus === 'verified' ? 'success' : 'warning'}
                size="small"
              />
              {rider.phoneVerified && (
                <Chip label="Phone Verified" color="success" size="small" variant="outlined" />
              )}
            </Box>
          </Box>
        </Stack>

        {/* Stats Cards */}
        <Grid container spacing={{ xs: 1, md: 2 }} wrap="wrap" sx={{ pb: 1 }}>
          <Grid item xs={6} sm={4} md sx={{ minWidth: 0 }}>
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 1.25, md: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <AccountBalanceIcon color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    Total SD Wallet
                  </Typography>
                </Box>
                <Typography variant="h5">
                  ₹{sdWallet?.totalSd?.toLocaleString() ?? 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md sx={{ minWidth: 0 }}>
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 1.25, md: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <MoneyOffIcon color="warning" />
                  <Typography variant="body2" color="text.secondary">
                    Pending SD
                  </Typography>
                </Box>
                <Typography variant="h5">
                  ₹{sdWallet?.pendingSd?.toLocaleString() ?? 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md sx={{ minWidth: 0 }}>
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 1.25, md: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CurrencyRupeeIcon color="success" />
                  <Typography variant="body2" color="text.secondary">
                    Fixed Deposit
                  </Typography>
                </Box>
                <Typography variant="h5">
                  {sdWallet?.fixedDeposit != null ? `₹${sdWallet.fixedDeposit.toLocaleString()}` : 'Not set'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md sx={{ minWidth: 0 }}>
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 1.25, md: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ReceiptIcon color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    Total Orders
                  </Typography>
                </Box>
                <Typography variant="h5">
                  {rider.totalOrders || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4} md sx={{ minWidth: 0 }}>
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 1.25, md: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CurrencyRupeeIcon color="success" />
                  <Typography variant="body2" color="text.secondary">
                    Total Earnings
                  </Typography>
                </Box>
                <Typography variant="h5">
                  ₹{rider.totalEarnings?.toLocaleString() || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Registration Status Completer */}
      {riderId && (
        <RiderRegistrationCompleter
          riderId={riderId}
          onRegistrationUpdated={fetchRiderDetails}
        />
      )}

      {/* Payment Block Status Warning */}
      {paymentBlockStatus?.isBlocked && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setUnblockDialogOpen(true)}
              startIcon={<MoneyOffIcon />}
            >
              Unblock Payment
            </Button>
          }
        >
          <Typography variant="body2" fontWeight="bold">
            Payment Blocked - Maximum Attempts Exceeded
          </Typography>
          <Typography variant="body2">
            Blocked {paymentBlockStatus.attemptCount} attempts |
            {paymentBlockStatus.hoursRemaining
              ? ` ${Math.floor(paymentBlockStatus.hoursRemaining)}h ${Math.round((paymentBlockStatus.hoursRemaining % 1) * 60)}m remaining`
              : ' Expired'}
          </Typography>
        </Alert>
      )}

      {/* Unblock Payment Dialog */}
      <Dialog open={unblockDialogOpen} onClose={() => setUnblockDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Unblock Payment</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will reset the payment attempt counter and allow the rider to make payments again.
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for Unblocking *"
            value={unblockReason}
            onChange={(e) => setUnblockReason(e.target.value)}
            placeholder="E.g., Payment gateway issue resolved, rider verified"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnblockDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUnblockPayment}
            variant="contained"
            color="primary"
            disabled={unblocking || !unblockReason.trim()}
            startIcon={unblocking ? <CircularProgress size={16} /> : <MoneyOffIcon />}
          >
            {unblocking ? 'Unblocking...' : 'Unblock Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2, boxShadow: 2, position: 'sticky', top: 190, zIndex: 99, backgroundColor: 'background.paper' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="rider detail tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              minHeight: 64,
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 500,
              '&.Mui-selected': {
                fontWeight: 600,
              },
            },
          }}
        >
          <Tab label="Profile" icon={<PersonIcon />} iconPosition="start" {...a11yProps(0)} />
          <Tab label="KYC Documents" icon={<VerifiedUserIcon />} iconPosition="start" {...a11yProps(1)} />
          <Tab label="Bank Details" icon={<AccountBalanceIcon />} iconPosition="start" {...a11yProps(2)} />
          <Tab label="EV Rental" icon={<TwoWheelerIcon />} iconPosition="start" {...a11yProps(3)} />
          <Tab label="Client Mappings" icon={<BusinessIcon />} iconPosition="start" {...a11yProps(4)} />
          <Tab label="Earnings" icon={<CurrencyRupeeIcon />} iconPosition="start" {...a11yProps(5)} />
          <Tab label="Deductions" icon={<MoneyOffIcon />} iconPosition="start" {...a11yProps(6)} />
          <Tab label="Payment History" icon={<PaymentIcon />} iconPosition="start" {...a11yProps(7)} />
          <Tab label="Vehicle History" icon={<HistoryIcon />} iconPosition="start" {...a11yProps(8)} />
          <Tab label="Order History" icon={<ReceiptIcon />} iconPosition="start" {...a11yProps(9)} />
          <Tab label="Service Tickets" icon={<BuildIcon />} iconPosition="start" {...a11yProps(10)} />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      {/* Index 0: Profile */}
      <TabPanel value={tabValue} index={0}>
        <RiderProfileTab rider={rider} onUpdateProfile={handleSaveProfileSection} onUpdateKYCDetails={handleSaveProfileSection} />
      </TabPanel>

      {/* Index 1: KYC Documents */}
      <TabPanel value={tabValue} index={1}>
        <RiderKYCTab
          riderId={riderId || ''}
          kycDocuments={kycDocuments}
          kycStatus={rider.kycStatus}
          loading={kycLoading}
          onViewDocument={(url, title) =>
            setDocumentPreviewDialog({ open: true, documentUrl: url, documentType: title })
          }
          onVerifyDocument={(kyc: RiderKYC) => setKycVerificationDialog({ open: true, kyc })}
          onKYCRefresh={fetchKYCDocuments}
          onRequestDocuments={() => {
            enqueueSnackbar('Document request feature coming soon', { variant: 'info' });
          }}
          onUploadDocument={handleKYCUpload}
        />
      </TabPanel>

      {/* Index 2: Bank Details */}
      <TabPanel value={tabValue} index={2}>
        {riderId && <BankDetailsSection riderId={riderId} />}
      </TabPanel>

      {/* Index 3: EV Rental */}
      <TabPanel value={tabValue} index={3}>
        <Box>
          {rentalLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : currentRental ? (
            <Box>
              {/* Rental Payment Tab */}
              <RentalPaymentTab
                riderId={riderId!}
                rental={currentRental}
                onPaymentUpdated={handlePaymentUpdated}
              />
            </Box>
          ) : (
            <Box sx={{ px: 3, pb: 3 }}>
              <Grid container spacing={3}>
                {/* Info Alert at Top */}
                <Grid item xs={12}>
                  <Alert severity="info">
                    Select a vehicle preference to enable EV rental for this rider. The rider can then be assigned a rental vehicle.
                  </Alert>
                </Grid>

                {/* EV Rental Preference Section - Full Width at Top */}
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        EV Rental Preference
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      {/* Warning Alert when Vehicle is Assigned */}
                      {rider.assignedVehicle && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>Vehicle Currently Assigned:</strong> {rider.assignedVehicle.registrationNumber}{' '}
                            ({rider.assignedVehicle.make} {rider.assignedVehicle.model})
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            EV Rental preference is locked to <strong>"Yes"</strong> while a vehicle is assigned.
                            To change this to "No", please unassign the vehicle first.
                          </Typography>
                        </Alert>
                      )}

                      <VehiclePreferenceSelector
                        riderId={riderId!}
                        needsEvRental={rider.needsEvRental ?? false}
                        currentPreference={(rider.vehiclePreference as any) ?? undefined}
                        currentModelId={rider.preferredVehicleModelId ?? undefined}
                        currentOwnVehicleType={(rider.ownVehicleType as any) ?? undefined}
                        onPreferenceSelected={handlePreferenceSelected}
                        disabled={!!rider.assignedVehicle}
                      />

                      {/* Helper text when vehicle is assigned */}
                      {rider.assignedVehicle && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Vehicle assigned: {rider.assignedVehicle.registrationNumber} - Cannot change to "No" until vehicle is unassigned
                        </Typography>
                      )}

                      <Alert severity={rider.ownVehicleType ? 'success' : 'info'} sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>Vehicle Ownership:</strong> {getVehicleOwnership(rider.ownVehicleType)}
                        </Typography>
                      </Alert>

                      {rider.needsEvRental === true && rider.kycStatus === 'approved' && !rider.assignedVehicle && (
                        <Box
                          sx={{
                            mt: 2,
                            p: { xs: 2, sm: 3 },
                            borderRadius: 2.5,
                            border: '1px solid rgba(25, 118, 210, 0.24)',
                            background: 'linear-gradient(145deg, rgba(25,118,210,0.08), rgba(255,255,255,0.96) 68%)',
                            boxShadow: '0 8px 24px rgba(25, 118, 210, 0.08)',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
                            <Box>
                              <Typography variant="overline" color="primary.main" fontWeight={800} letterSpacing={1}>
                                Vehicle Operations
                              </Typography>
                              <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                                Raise deployment ticket
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                Capture the deposit and business reason before requesting vehicle allocation.
                              </Typography>
                            </Box>
                            <Chip
                              label="EV rental required"
                              color="success"
                              size="small"
                              sx={{ fontWeight: 700, mt: 0.5 }}
                            />
                          </Box>

                          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.25 }}>
                            Ticket details
                          </Typography>
                          <Alert severity="info" sx={{ mb: 2 }}>
                            SD is mandatory for New Rider. For Re-Joiner and Vehicle Exchange, SD is not mandatory.
                          </Alert>
                          <Grid container spacing={2} alignItems="flex-start">
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Fixed security deposit"
                                type= "number"
                                value={deploymentTicketFixedSecurityDeposit}
                                onChange={(e) => setDeploymentTicketFixedSecurityDeposit(e.target.value)}
                                placeholder="1,000"
                                inputProps={{ min: 0, step: '1' }}
                                InputProps={{ startAdornment: <CurrencyRupeeIcon fontSize="small" sx={{ mr: 0.75, color: 'text.secondary' }} /> }}   
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Paying security deposit"
                                type="number"
                                value={deploymentTicketPayingSD}
                                onChange={(e) => setDeploymentTicketPayingSD(e.target.value)}
                                placeholder="600"
                                inputProps={{ min: 0, step: '1' }}
                                InputProps={{ startAdornment: <CurrencyRupeeIcon fontSize="small" sx={{ mr: 0.75, color: 'text.secondary' }} /> }}
                               
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <FormControl fullWidth size="small" required>
                                <InputLabel id="deployment-ticket-reason-label">Ticket reason</InputLabel>
                                <Select
                                  labelId="deployment-ticket-reason-label"
                                  value={deploymentTicketReason}
                                  label="Ticket reason"
                                  onChange={(e) => setDeploymentTicketReason(e.target.value)}
                                >
                                  <MenuItem value="NEW_RIDER">New Rider - Vehicle Deployment</MenuItem>
                                  <MenuItem value="RE_JOINER">Re-Joiner - Vehicle Deployment</MenuItem>
                                  <MenuItem value="VEHICLE_EXCHANGE">Vehicle Exchange</MenuItem>
                                </Select>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, ml: 1.75 }}>
                                  Select the operational reason
                                </Typography>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, pt: { xs: 0.5, sm: 1 } }}>
                              <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                onClick={handleRaiseDeploymentTicket}
                                disabled={raisingDeploymentTicket}
                                sx={{
                                  maxWidth: { xs: '100%', sm: 280 },
                                  minHeight: 44,
                                  px: 3,
                                  fontWeight: 800,
                                  boxShadow: '0 6px 14px rgba(25, 118, 210, 0.22)',
                                }}
                              >
                                {raisingDeploymentTicket ? 'Submitting ticket...' : 'Raise deployment ticket'}
                              </Button>
                            </Grid>
                          </Grid>
                        </Box>
                      )}

                      {rider.assignedVehicle && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 2.5,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'warning.main',
                            background: 'linear-gradient(135deg, rgba(255,152,0,0.06), rgba(244,67,54,0.03))',
                            boxShadow: '0 6px 18px rgba(255, 152, 0, 0.05)',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                            <Box>
                              <Typography variant="subtitle1" fontWeight={700}>
                                Raise Return Ticket
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Vehicle is currently assigned. Raise a return request for this rider.
                              </Typography>
                            </Box>
                            <Chip
                              label="Vehicle Assigned"
                              color="warning"
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>

                          <Button
                            variant="contained"
                            color="warning"
                            size="medium"
                            onClick={() => setReturnTicketDialogOpen(true)}
                            disabled={raisingReturnTicket}
                            sx={{
                              minWidth: { xs: '100%', sm: 220 },
                              px: 3,
                              py: 1,
                              fontWeight: 600,
                            }}
                          >
                            {raisingReturnTicket ? 'Raising...' : 'Raise Return Ticket'}
                          </Button>
                        </Box>
                      )}

                      {!rider.assignedVehicle && rider.needsEvRental !== true && (
                        <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px dashed', borderColor: 'divider', bgcolor: 'action.hover' }}>
                          <Typography variant="body2" color="text.secondary">
                            Deployment Ticket is available only when the rider has selected <strong>Yes</strong> for EV Rental preference.
                          </Typography>
                        </Box>
                      )}

                      {rider.assignedVehicle && (
                        <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px dashed', borderColor: 'divider', bgcolor: 'action.hover' }}>
                          <Typography variant="body2" color="text.secondary">
                            Return Ticket is enabled because a vehicle is currently assigned to this rider.
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Vehicle Assignment Section - Full Width Below */}
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="h6">Vehicle Assignment</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {rider.assignedVehicle && (rider.NoOfReturnTickets ?? 0) === 1 ? (
                            <Button variant="outlined" color="error" size="small" onClick={handleOpenUnassignmentDialog}>
                              Unassign
                            </Button>
                          ) : null}

                          {!rider.assignedVehicle && (
                            <Button
                              variant="contained"
                              startIcon={<TwoWheelerIcon />}
                              size="small"
                              onClick={handleAssignVehicle}
                              disabled={isVehicleAssignmentDisabled}
                            >
                              Assign Vehicle
                            </Button>
                          )}
                        </Box>
                      </Box>

                      {rider.assignedVehicle && (rider.NoOfReturnTickets ?? 0) !== 1 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            • Exactly <strong>one return ticket</strong> is required to unassign this vehicle. Current return tickets: <strong>{rider.NoOfReturnTickets ?? 0}</strong>
                          </Typography>
                        </Alert>
                      )}

                      {/* Show warning if vehicle assignment is disabled */}
                      {!rider.assignedVehicle && isVehicleAssignmentDisabled && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          {!isClientMappingValid(rider.clientName, rider.clientRiderId) && (
                            <Typography variant="body2">
                              • Rider must have a valid <strong>Client Name</strong> and <strong>Client Rider ID</strong> before assigning a vehicle. Current values: <strong>{rider.clientName || 'Not available'}</strong> / <strong>{rider.clientRiderId || 'Not available'}</strong>
                            </Typography>
                          )}
                          {!rider.needsEvRental && (
                            <Typography variant="body2">
                              • Vehicle preference must be set to <strong>"Yes"</strong> to enable vehicle assignment. Please update the EV Rental Preference above.
                            </Typography>
                          )}
                          {rider.kycStatus !== 'approved' && (
                            <Typography variant="body2">
                              • Rider KYC status must be <strong>approved</strong> before assigning a vehicle. Current status: <strong>{rider.kycStatus}</strong>
                            </Typography>
                          )}
                          {rider.registrationStatus?.toLowerCase() !== 'completed' && (
                            <Typography variant="body2">
                              • Rider registration must be completed before assigning a vehicle. Current status: <strong>{rider.registrationStatus}</strong>
                            </Typography>
                          )}
                          {!rider.isActive && (
                            <Typography variant="body2">
                              • Rider account must be active to assign a vehicle. Please activate the rider first.
                            </Typography>
                          )}
                          {(rider.NoOfDeployedTickets ?? 0) !== 1 && (
                            <Typography variant="body2">
                              • Exactly <strong>one deployment ticket</strong> is required to assign a vehicle. Current deployed tickets: <strong>{rider.NoOfDeployedTickets ?? 0}</strong>
                            </Typography>
                          )}
                        </Alert>
                      )}

                      <Divider sx={{ mb: 2 }} />

                      {rider.assignedVehicle ? (
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Registration Number
                            </Typography>
                            <Link
                              component="button"
                              variant="body1"
                              fontWeight="medium"
                              onClick={() => handleNavigateToVehicle(rider.assignedVehicle!.id)}
                              sx={{
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' },
                                cursor: 'pointer',
                                textAlign: 'left',
                              }}
                            >
                              {rider.assignedVehicle.registrationNumber}
                            </Link>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Vehicle
                            </Typography>
                            <Typography variant="body1">
                              {rider.assignedVehicle.make} {rider.assignedVehicle.model}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Assigned Date
                            </Typography>
                            <Typography variant="body1">
                              {rider.assignedVehicle.assignedDate
                                ? new Date(rider.assignedVehicle.assignedDate).toLocaleDateString('en-IN')
                                : 'Not available'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Status
                            </Typography>
                            <Chip
                              label="Active"
                              color="success"
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          </Grid>
                        </Grid>
                      ) : (
                        <Alert severity="info">
                          No vehicle currently assigned. Use "Assign EV Rental Vehicle" button above after selecting a vehicle preference.
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </TabPanel>

      {/* Index 4: Client Mappings */}
      <TabPanel value={tabValue} index={4}>
        <RiderClientMappingsTab riderId={riderId!} />
      </TabPanel>

      {/* Index 5: Earnings */}
      <TabPanel value={tabValue} index={5}>
        <RiderEarningsTab
          earnings={earnings}
          earningsSummary={earningsSummary}
        />
      </TabPanel>

      {/* Index 6: Deductions */}
      <TabPanel value={tabValue} index={6}>
        <RiderDeductionsTab deductions={deductions} />
      </TabPanel>

      {/* Index 7: Payment History */}
      <TabPanel value={tabValue} index={7}>
        <RiderPaymentHistoryTab payments={paymentHistory} loading={paymentHistoryLoading} />
      </TabPanel>

      {/* Index 8: Vehicle History */}
      <TabPanel value={tabValue} index={8}>
        <RiderVehicleHistoryTab
          vehicleHistory={vehicleHistory}
          loading={vehicleHistoryLoading}
          onVehicleHistoryUpdated={fetchVehicleHistory}
        />
      </TabPanel>

      {/* Index 9: Order History */}
      <TabPanel value={tabValue} index={9}>
        {ordersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Filters and Sort Controls */}
            <Paper sx={{ p: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="From"
                    type="date"
                    value={filterDateFrom ?? ''}
                    onChange={(e) => setFilterDateFrom(e.target.value || undefined)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="To"
                    type="date"
                    value={filterDateTo ?? ''}
                    onChange={(e) => setFilterDateTo(e.target.value || undefined)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={4}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" color="primary" onClick={handleApplyFilters}>
                      Apply Filters
                    </Button>
                    <Button variant="outlined" color="secondary" onClick={handleClearFilters}>
                      Clear
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
            <RiderOrderHistoryTab orderHistory={orderHistory} />
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
              <Pagination
                count={orderTotalPages}
                page={orderPage}
                onChange={(_e, page) => fetchOrderHistory(page)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </Box>
        )}
      </TabPanel>

      {/* Index 10: Service Tickets */}
      <TabPanel value={tabValue} index={10}>
        {rider?.publicRiderId ? (
          <RiderServiceTicketsTab
            riderId={rider.publicRiderId}
            riderDatabaseId={rider.id}
          />
        ) : (
          <Alert severity="info">A public rider ID is required to load service tickets.</Alert>
        )}
      </TabPanel>

      {/* Dialogs */}
      <DocumentPreviewDialog
        open={documentPreviewDialog.open}
        url={documentPreviewDialog.documentUrl}
        title={documentPreviewDialog.documentType}
        onClose={() =>
          setDocumentPreviewDialog({ open: false, documentUrl: null, documentType: '' })
        }
      />

      <KYCVerificationDialog
        open={kycVerificationDialog.open}
        kyc={kycVerificationDialog.kyc}
        onClose={() => setKycVerificationDialog({ open: false, kyc: null })}
        onVerify={handleKYCVerify}
      />

      <VehicleAssignmentDialog
        open={vehicleAssignmentDialog.open}
        availableHubs={hubs}
        availableVehicles={vehicles}
        selectedHub={selectedHub}
        selectedVehicle={selectedVehicle}
        vehiclesLoading={vehicleSearchLoading}
        dailyRent={dailyRent}
        dailyRentLoading={dailyRentLoading}
        vehicleSearchOpen={vehicleSearchOpen}
        onVehicleSearchChange={(searchText) => {
          setVehicleSearch(searchText);
          setVehiclePage(1);
          fetchAvailableVehicles({ hubId: selectedHub, search: searchText, page: 1 });
        }}
        onVehicleSearchOpen={() => setVehicleSearchOpen(true)}
        onVehicleSearchClose={() => setVehicleSearchOpen(false)}
        onHubChange={(hubId) => {
          console.log('🔄 [RiderDetail] Hub changed to:', hubId);
          setSelectedHub(hubId);
          setSelectedVehicle('');
          setDailyRent(null);
          setVehicleSearch('');
          setVehiclePage(1);
          console.log('🔄 [RiderDetail] Fetching vehicles for hub:', hubId);
          fetchAvailableVehicles({ hubId, page: 1 });
        }}
        onVehicleChange={(vehicleId) => {
          console.log('🔄 [RiderDetail] Vehicle changed to:', vehicleId);
          setSelectedVehicle(vehicleId);
          if (vehicleId && selectedHub) {
            fetchFixedDailyRent(vehicleId, selectedHub);
          } else {
            setDailyRent(null);
          }
        }}
        onLoadMoreVehicles={async () => {
          if (vehiclePage < vehicleTotalPages && !vehicleSearchLoading) {
            const nextPage = vehiclePage + 1;
            setVehiclePage(nextPage);
            await fetchAvailableVehicles({
              hubId: selectedHub,
              search: vehicleSearch,
              page: nextPage,
            });
          }
        }}
        hasMoreVehicles={vehiclePage < vehicleTotalPages}
        assigning={assignmentLoading}
        onAssign={handleVehicleAssignment}
        onClose={() => {
          console.log('❌ [RiderDetail] Closing vehicle assignment dialog');
          setVehicleAssignmentDialog({ open: false });
          setSelectedHub('');
          setSelectedVehicle('');
          setDailyRent(null);
          setDailyRentLoading(false);
          setVehicleSearch('');
          setVehiclePage(1);
          setVehicleTotalPages(1);
        }}
      />

      {/* Vehicle Unassignment Dialog */}
      <VehicleUnassignmentDialog
        open={vehicleUnassignmentDialog.open}
        onClose={() => setVehicleUnassignmentDialog({ open: false })}
        onConfirm={handleUnassignVehicle}
        vehicleInfo={
          rider?.assignedVehicle
            ? {
                registrationNumber: rider.assignedVehicle.registrationNumber,
                make: rider.assignedVehicle.make,
                model: rider.assignedVehicle.model,
              }
            : undefined
        }
        pendingAmount={pendingEarnings}
        publicRiderId={rider?.publicRiderId}
        riderProfileId={riderId}
      />

      <Dialog
        open={returnTicketDialogOpen}
        onClose={() => {
          if (!raisingReturnTicket) {
            setReturnTicketDialogOpen(false);
            setReturnTicketFeedback('');
            setReturnTicketUnassignmentReason('');
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Raise Return Ticket</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the reason for returning this vehicle.
          </Typography>
          <FormControl fullWidth required sx={{ mb: 2 }}>
            <InputLabel id="return-ticket-reason-label">Return reason</InputLabel>
            <Select
              labelId="return-ticket-reason-label"
              value={returnTicketReason}
              label="Return reason"
              onChange={(event) => setReturnTicketReason(event.target.value)}
              disabled={raisingReturnTicket}
            >
              {RETURN_TICKET_REASONS.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {reason}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {returnTicketReason && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {RETURN_TICKET_REASON_NOTES[returnTicketReason as keyof typeof RETURN_TICKET_REASON_NOTES]}
            </Typography>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="return-ticket-feedback-label">Rider Feedback</InputLabel>
            <Select
              labelId="return-ticket-feedback-label"
              value={returnTicketFeedback}
              label="Rider Feedback"
              onChange={(event) => setReturnTicketFeedback(event.target.value)}
              disabled={raisingReturnTicket}
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="Found another job">Found another job</MenuItem>
              <MenuItem value="Issues with hub incharge">Issues with hub incharge</MenuItem>
              <MenuItem value="Not interest in Delivery job">Not interest in Delivery job</MenuItem>
              <MenuItem value="Not satisfied with Client">Not satisfied with Client</MenuItem>
              <MenuItem value="Not satisfied with Rate">Not satisfied with Rate</MenuItem>
              <MenuItem value="Not satisfied with Vehicle Performance">Not satisfied with Vehicle Performance</MenuItem>
              <MenuItem value="Personal issues">Personal issues</MenuItem>
              <MenuItem value="Willing to work with another client">Willing to work with another client</MenuItem>
              <MenuItem value="EV Returned - Converted to ICE vehicle/Own Vehicle">EV Returned - Converted to ICE vehicle/Own Vehicle</MenuItem>
              <MenuItem value="Recovery Vehicles">Recovery Vehicles</MenuItem>
              <MenuItem value="Exchanged (Swapping)">Exchanged (Swapping)</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Unassignment Reason"
            value={returnTicketUnassignmentReason}
            onChange={(event) => setReturnTicketUnassignmentReason(event.target.value)}
            disabled={raisingReturnTicket}
            placeholder="Optional reason for unassignment"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setReturnTicketDialogOpen(false);
              setReturnTicketFeedback('');
              setReturnTicketUnassignmentReason('');
            }}
            disabled={raisingReturnTicket}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => void handleRaiseReturnTicket()}
            disabled={raisingReturnTicket || !returnTicketReason}
          >
            {raisingReturnTicket ? 'Raising...' : 'Raise Return Ticket'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Rider Dialog */}
      <EnhancedRiderForm
        open={editRiderDialog}
        rider={rider}
        onClose={handleCloseEditRider}
        onSave={handleSaveRider}
      />

      {/* Set Fixed Deposit Dialog */}
      {riderId && rider && (
        <SetFixedDepositDialog
          open={setFixedDepositDialog}
          riderId={riderId}
          riderName={rider.name || 'Rider'}
          onClose={() => setSetFixedDepositDialog(false)}
          onSuccess={(amount) => {
            enqueueSnackbar(`Fixed Deposit of ₹${amount.toFixed(2)} has been set successfully`, {
              variant: 'success',
            });
          }}
        />
      )}

      {/* Create Paylink Dialog */}
      {rider && (
        <CreatePaylinkDialog
          open={createPaylinkDialog}
          rider={rider}
          pendingSd={sdWallet?.pendingSd ?? 0}
          onClose={() => setCreatePaylinkDialog(false)}
          onSuccess={() => {
            enqueueSnackbar('Paylink created successfully', {
              variant: 'success',
            });
            // Optionally refresh some data if needed
          }}
        />
      )}

      {/* EV Rental Assignment Dialog */}
      {riderId && (
        <EVRentalAssignmentDialog
          open={rentalAssignmentDialog}
          riderId={riderId}
          riderName={rider.name || 'Rider'}
          preferredModelId={rider.preferredVehicleModelId ?? undefined}
          onClose={() => setRentalAssignmentDialog(false)}
          onRentalCreated={handleRentalCreated}
        />
      )}
    </Box>
  );
};

export default RiderDetail;
